import { Controller } from "@hotwired/stimulus"
import Swal from "sweetalert2"
import { Calendar } from "fullcalendar"
import interactionPlugin from "fullcalendar/interaction"
import timeGridPlugin from "fullcalendar/timegrid"
import themePlugin from "fullcalendar/themes/classic"

export default class extends Controller {
  static values = {
    weekStartDay: { type: Number, default: 1 }
  }

  static targets = [
    "calendar",
    "eventsData",
    "closedHoursData",
    "tenantsData",
    "areasData",
    "resourceFilter",
    "resourceFilterLabel",
    "form",
    "methodInput",
    "tenantIdInput",
    "weekdayInput",
    "startTimeInput",
    "endTimeInput",
    "areaIdInput",
    "spaceIdInput",
    "commentInput",
    "deleteForm"
  ]

  connect() {
    this.events = this.parseJson(this.eventsDataTarget)
    this.closedHourEvents = this.parseJson(this.closedHoursDataTarget)
    this.tenants = this.parseJson(this.tenantsDataTarget)
    this.areas = this.parseJson(this.areasDataTarget)

    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [themePlugin, interactionPlugin, timeGridPlugin],
      initialView: "timeGridWeek",
      initialDate: this.startOfCurrentWeek(),
      firstDay: this.weekStartDayValue,
      allDaySlot: false,
      height: "auto",
      selectable: true,
      slotDuration: "00:15:00",
      snapDuration: "00:15:00",
      slotLabelInterval: "01:00:00",
      slotMinTime: "00:00:00",
      slotMaxTime: "24:00:00",
      dayHeaderContent: (arg) => this.dayHeaderContent(arg),
      headerToolbar: {
        left: "",
        center: "",
        right: ""
      },
      events: this.filteredEvents(),
      selectOverlap: (event) => event.extendedProps.editorType !== "closed_hours",
      selectAllow: (selectionInfo) => !this.overlapsClosedHours(
        this.mondayWeekday(selectionInfo.start),
        this.timeString(selectionInfo.start),
        this.selectionEndTimeString(selectionInfo.start, selectionInfo.end)
      ),
      select: (selectionInfo) => this.createOrEditFromSelection(selectionInfo),
      eventClick: (clickInfo) => this.editExistingEvent(clickInfo.event)
    })

    this.calendar.render()
    this.updateResourceFilterLabel()
  }

  disconnect() {
    this.calendar?.destroy()
  }

  resourceFilterChanged() {
    this.refreshEvents()
    this.updateResourceFilterLabel()
  }

  async createOrEditFromSelection(selectionInfo) {
    const payload = {
      weekday: this.mondayWeekday(selectionInfo.start),
      startTime: this.timeString(selectionInfo.start),
      endTime: this.selectionEndTimeString(selectionInfo.start, selectionInfo.end),
      tenantId: "",
      areaId: this.selectedResourceAreaId(),
      spaceId: this.selectedResourceSpaceId(),
      comment: ""
    }

    await this.openDialog(payload)
  }

  async editExistingEvent(event) {
    const props = event.extendedProps
    if (props.editorType === "closed_hours") return

    if (props.grouped) {
      await Swal.fire({
        title: "Grouped recurring booking",
        text: `${event.title} represents ${props.recordIds?.length || 0} recurring resource bookings for the same tenant and weekly time.`,
        confirmButtonText: "Close"
      })
      return
    }

    const payload = {
      recordId: props.recordId,
      weekday: props.weekday,
      startTime: props.startTime.slice(0, 5),
      endTime: props.endTime.slice(0, 5),
      tenantId: String(props.tenantId || ""),
      areaId: String(props.bookableAreaId || ""),
      spaceId: String(props.bookableSpaceId || ""),
      comment: props.comment || ""
    }

    await this.openDialog(payload, true)
  }

  async openDialog(payload, editing = false) {
    const tenantOptions = this.optionHtml(this.tenants, payload.tenantId)
    const areaOptions = this.optionHtml(this.areas.map((area) => ({ id: area.id, name: area.name })), payload.areaId, "Venue-wide")
    const spaceOptions = this.spaceOptionHtml(payload.areaId, payload.spaceId)
    const timeOptions = this.timeOptionsHtml()

    const result = await Swal.fire({
      title: editing ? "Edit recurring booking" : "New recurring booking",
      html: `
        <div class="grid gap-3 text-left">
          <label class="text-sm font-semibold text-stone-700">Tenant</label>
          <select id="swal-tenant" class="swal2-input booking-calendar-dialog-select !mt-0">${tenantOptions}</select>
          <label class="text-sm font-semibold text-stone-700">Weekday</label>
          <select id="swal-weekday" class="swal2-input booking-calendar-dialog-select !mt-0">
            ${this.weekdayOptionsHtml(payload.weekday)}
          </select>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-sm font-semibold text-stone-700">Start</label>
              <select id="swal-start" class="swal2-input !mt-0" style="width: 9rem; min-width: 9rem; padding-right: 2rem;">${timeOptions}</select>
            </div>
            <div>
              <label class="text-sm font-semibold text-stone-700">End</label>
              <select id="swal-end" class="swal2-input !mt-0" style="width: 9rem; min-width: 9rem; padding-right: 2rem;">${timeOptions}</select>
            </div>
          </div>
          <label class="text-sm font-semibold text-stone-700">Bookable area</label>
          <select id="swal-area" class="swal2-input booking-calendar-dialog-select !mt-0">${areaOptions}</select>
          <label class="text-sm font-semibold text-stone-700">Bookable space</label>
          <select id="swal-space" class="swal2-input booking-calendar-dialog-select !mt-0">${spaceOptions}</select>
          <label class="text-sm font-semibold text-stone-700">Comment</label>
          <textarea id="swal-comment" class="swal2-textarea !mt-0" placeholder="Optional note">${payload.comment}</textarea>
        </div>
      `,
      showCancelButton: true,
      showDenyButton: editing,
      confirmButtonText: editing ? "Save changes" : "Create booking",
      denyButtonText: "Delete",
      cancelButtonText: "Cancel",
      focusConfirm: false,
      width: 640,
      didOpen: () => {
        document.getElementById("swal-start").value = payload.startTime
        document.getElementById("swal-end").value = payload.endTime
        const areaSelect = document.getElementById("swal-area")
        const spaceSelect = document.getElementById("swal-space")
        areaSelect?.addEventListener("change", () => {
          const selectedAreaId = areaSelect.value
          spaceSelect.innerHTML = this.spaceOptionHtml(selectedAreaId, "")
        })
      },
      preConfirm: () => {
        const tenantId = document.getElementById("swal-tenant").value
        const weekday = document.getElementById("swal-weekday").value
        const startTime = document.getElementById("swal-start").value
        const endTime = document.getElementById("swal-end").value
        const areaId = document.getElementById("swal-area").value
        const spaceId = document.getElementById("swal-space").value
        const comment = document.getElementById("swal-comment").value

        if (!tenantId || !startTime || !endTime) {
          Swal.showValidationMessage("Tenant, start time, and end time are required.")
          return false
        }

        if (endTime <= startTime) {
          Swal.showValidationMessage("End time must be after the start time.")
          return false
        }

        if (this.overlapsClosedHours(weekday, startTime, endTime)) {
          Swal.showValidationMessage("This booking overlaps the venue's closing hours.")
          return false
        }

        return { tenantId, weekday, startTime, endTime, areaId, spaceId, comment }
      }
    })

    if (result.isDenied && editing) {
      this.deleteFormTarget.action = `${this.deleteFormTarget.dataset.urlBase}${payload.recordId}`
      this.deleteFormTarget.requestSubmit()
      return
    }

    if (!result.isConfirmed) return

    const values = result.value
    this.formTarget.action = editing ? `${this.formTarget.dataset.updateUrlBase}${payload.recordId}` : this.formTarget.dataset.createUrl
    this.methodInputTarget.value = editing ? "patch" : "post"
    this.tenantIdInputTarget.value = values.tenantId
    this.weekdayInputTarget.value = values.weekday
    this.startTimeInputTarget.value = values.startTime
    this.endTimeInputTarget.value = values.endTime
    this.areaIdInputTarget.value = values.areaId
    this.spaceIdInputTarget.value = values.spaceId
    this.commentInputTarget.value = values.comment
    this.formTarget.requestSubmit()
  }

  refreshEvents() {
    this.calendar.removeAllEvents()
    this.calendar.addEventSource(this.filteredEvents())
  }

  filteredEvents() {
    return [...this.closedHourEvents, ...this.events].filter((event) => this.eventMatchesSelectedResource(event))
  }

  eventMatchesSelectedResource(event) {
    const selected = this.selectedResource()
    if (selected.type === "all") return true

    const props = event.extendedProps || {}
    const areaId = props.bookableAreaId ? String(props.bookableAreaId) : ""
    const spaceId = props.bookableSpaceId ? String(props.bookableSpaceId) : ""

    if (selected.type === "venue") return areaId === "" && spaceId === ""
    if (areaId === "" && spaceId === "") return true
    if (selected.type === "area") return areaId === selected.id
    if (selected.type === "space") return (areaId === selected.areaId && spaceId === "") || spaceId === selected.id

    return true
  }

  selectedResource() {
    const value = this.resourceFilterTarget.value || "all"
    if (value === "all") return { type: "all", label: "all resources" }
    if (value === "venue") return { type: "venue", label: "full venue" }

    const [type, id] = value.split(":", 2)
    if (type === "area") {
      const area = this.areas.find((record) => String(record.id) === id)
      return { type, id, label: area?.name || "selected area" }
    }

    if (type === "space") {
      const area = this.areas.find((record) => record.spaces.some((space) => String(space.id) === id))
      const space = area?.spaces.find((record) => String(record.id) === id)
      return { type, id, areaId: area ? String(area.id) : "", label: area && space ? `${area.name} / ${space.name}` : "selected space" }
    }

    return { type: "all", label: "all resources" }
  }

  selectedResourceAreaId() {
    const selected = this.selectedResource()
    if (selected.type === "area") return selected.id
    if (selected.type === "space") return selected.areaId

    return ""
  }

  selectedResourceSpaceId() {
    const selected = this.selectedResource()
    return selected.type === "space" ? selected.id : ""
  }

  updateResourceFilterLabel() {
    const selected = this.selectedResource()
    this.resourceFilterLabelTarget.textContent = `Showing ${selected.label}`
  }

  optionHtml(records, selectedValue, blankLabel = "Select…") {
    const blank = `<option value="">${blankLabel}</option>`
    return blank + records.map((record) => `<option value="${record.id}" ${String(record.id) === String(selectedValue) ? "selected" : ""}>${this.escapeHtml(record.name)}</option>`).join("")
  }

  spaceOptionHtml(areaId, selectedValue) {
    const blank = `<option value="">Whole venue or area</option>`
    const spaces = this.areas
      .filter((area) => areaId === "" || String(area.id) === String(areaId))
      .flatMap((area) => area.spaces.map((space) => ({ id: space.id, name: `${area.name} / ${space.name}` })))

    return blank + spaces.map((space) => `<option value="${space.id}" ${String(space.id) === String(selectedValue) ? "selected" : ""}>${this.escapeHtml(space.name)}</option>`).join("")
  }

  weekdayOptionsHtml(selectedValue) {
    return ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
      .map((name, index) => `<option value="${index}" ${String(index) === String(selectedValue) ? "selected" : ""}>${name}</option>`)
      .join("")
  }

  timeOptionsHtml() {
    const options = []

    for (let minute = 0; minute <= 24 * 60; minute += 15) {
      const hours = Math.floor(minute / 60)
      const remainder = minute % 60
      const label = `${String(hours).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
      options.push(`<option value="${label}">${label}</option>`)
    }

    return options.join("")
  }

  overlapsClosedHours(weekday, startTime, endTime) {
    const startMinute = this.minutesFromTime(startTime)
    const endMinute = this.minutesFromTime(endTime)

    return this.closedHourEvents.some((event) => {
      const props = event.extendedProps || {}
      if (String(props.weekday) !== String(weekday)) return false

      return this.rangesOverlap(
        startMinute,
        endMinute,
        this.minutesFromTime(props.startTime || event.startTime),
        this.minutesFromTime(props.endTime || event.endTime)
      )
    })
  }

  rangesOverlap(startMinute, endMinute, closedStartMinute, closedEndMinute) {
    return startMinute < closedEndMinute && endMinute > closedStartMinute
  }

  minutesFromTime(time) {
    const [hours, minutes] = String(time || "00:00").split(":").map((part) => Number.parseInt(part, 10))
    return (hours || 0) * 60 + (minutes || 0)
  }

  parseJson(element) {
    try {
      return JSON.parse(element.textContent || "[]")
    } catch (_error) {
      return []
    }
  }

  timeString(date) {
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
  }

  selectionEndTimeString(startDate, endDate) {
    if (
      endDate.getHours() === 0 &&
      endDate.getMinutes() === 0 &&
      endDate.getTime() > startDate.getTime()
    ) {
      return "24:00"
    }

    return this.timeString(endDate)
  }

  mondayWeekday(date) {
    return (date.getDay() + 6) % 7
  }

  startOfCurrentWeek() {
    const today = new Date()
    const clone = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    clone.setDate(clone.getDate() - ((clone.getDay() - this.weekStartDayValue + 7) % 7))
    return clone
  }

  dayHeaderContent(arg) {
    const weekday = new Intl.DateTimeFormat(undefined, { weekday: "long" }).format(arg.date)

    return {
      html: `
        <div class="flex flex-col items-center">
          <span>${this.escapeHtml(weekday)}</span>
        </div>
      `
    }
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll("\"", "&quot;")
      .replaceAll("'", "&#39;")
  }
}
